import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check } from "lucide-react";
import API from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

export default function NotificationBell() {
  const { user } = useAuth();
  const { t, locale } = useLanguage();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);

  const fetchData = useCallback(async () => {
    if (!user) return;
    try {
      const [notifs, count] = await Promise.all([
        API.get("/notifications"),
        API.get("/notifications/unread-count")
      ]);
      setNotifications(notifs.data.notifications || []);
      setUnreadCount(count.data.count || 0);
    } catch {}
  }, [user]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [user, fetchData]);

  const markAllRead = async () => {
    try {
      await API.put("/notifications/read-all");
      setUnreadCount(0);
      setNotifications((n) => n.map((x) => ({ ...x, read: true })));
    } catch {}
  };

  const handleNotificationClick = async (notif) => {
    // Mark this notification as read
    if (!notif.read) {
      try {
        await API.put(`/notifications/${notif.id}/read`);
        setNotifications((prev) =>
          prev.map((n) => (n.id === notif.id ? { ...n, read: true } : n))
        );
        setUnreadCount((c) => Math.max(0, c - 1));
      } catch {}
    }

    // Close the dropdown
    setOpen(false);

    // Navigate based on notification type and data
    const data = notif.data || {};
    const orderId = data.order_id;
    const isAdmin = user?.role === "admin";

    switch (notif.type) {
      case "new_order":
      case "payment_uploaded":
        // Admin-targeted notifications -> admin orders page
        if (orderId) {
          navigate(`/admin/orders?order_id=${encodeURIComponent(orderId)}`);
        } else {
          navigate("/admin/orders");
        }
        break;
      case "order_created":
      case "order_status":
        // User-targeted notifications -> user orders page (or admin if admin)
        if (orderId) {
          if (isAdmin) {
            navigate(`/admin/orders?order_id=${encodeURIComponent(orderId)}`);
          } else {
            navigate(`/orders/${orderId}`);
          }
        } else {
          navigate(isAdmin ? "/admin/orders" : "/orders");
        }
        break;
      default:
        // Fallback: if there's an order_id, navigate to orders
        if (orderId) {
          navigate(isAdmin ? `/admin/orders?order_id=${encodeURIComponent(orderId)}` : `/orders/${orderId}`);
        }
        break;
    }
  };

  if (!user) return null;

  const typeIcons = {
    order_created: "🛒",
    new_order: "📦",
    order_status: "🔄",
    payment_uploaded: "💳",
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger className="relative" data-testid="notification-bell">
        <Bell className="w-5 h-5 text-stone-600 hover:text-stone-900 transition-colors" />
        {unreadCount > 0 && (
          <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center" data-testid="notification-count">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 bg-white p-0 max-h-[400px] overflow-hidden" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100">
          <span className="text-sm font-semibold text-stone-800">{t("notifications.title")}</span>
          {unreadCount > 0 && (
            <button onClick={markAllRead} className="text-xs text-primary hover:opacity-80 flex items-center gap-1" data-testid="mark-all-read-btn">
              <Check className="w-3 h-3" /> {t("notifications.mark_all_read")}
            </button>
          )}
        </div>
        <div className="overflow-y-auto max-h-[340px]">
          {notifications.length === 0 ? (
            <div className="p-6 text-center text-sm text-stone-400">{t("notifications.no_notifs")}</div>
          ) : (
            notifications.map((n) => (
              <div
                key={n.id}
                onClick={() => handleNotificationClick(n)}
                className={`px-4 py-3 border-b border-stone-50 cursor-pointer transition-colors hover:bg-stone-50 ${!n.read ? "bg-primary/5" : ""}`}
                data-testid={`notification-${n.id}`}
              >
                <div className="flex gap-2">
                  <span className="text-sm">{typeIcons[n.type] || "🔔"}</span>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${!n.read ? "font-medium text-stone-800" : "text-stone-600"}`}>{n.message}</p>
                    <p className="text-[10px] text-stone-400 mt-0.5">
                      {new Date(n.created_at).toLocaleString(locale === "id" ? "id-ID" : "en-US", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
